import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Checkbox, FormControlLabel, Box, CircularProgress, List, ListItem, Typography, ListItemText, Paper, Button, Snackbar } from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import MDBox from "components/MDBox";
import userPreferencesService from "services/UserPreferencesService";
import { useAuth } from "context/authContext";

export default function TabPreferences() {
    const { token, user, isAuthenticated } = useAuth();
    const [isEdit, setIsEdit] = useState(false);
    const [isSaved, setSaved] = useState(false);
    const [items, setItems] = useState([]);
    const [updatedItems, setUpdatedItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [checked, setChecked] = useState(false);
    const [open, setOpen] = useState(false);
    const [alert, setAlert] = useState({ type: '', message: '' });
    const navigate = useNavigate();

    useEffect(() => {
        try {
            setLoading(true);
            const init = async () => {
                const authenticated = await isAuthenticated();
                if (!authenticated) {
                    navigate(`/login`);
                    return;
                }

                await loadData();
            };

            init();
        } catch (error) {
            console.error("Error in useEffect of TabPreferences:", error);
            navigate(`/login`);
        }
    }, [navigate, user, isSaved]);

    const loadData = async () => {
        try {
            const response = await userPreferencesService.GetUserPreferences(user?.id, token);
            const data = await response.data.data;
            setChecked(data.some((item) => item?.Type === 1 && item?.Value === true));
            setItems(data);
        } catch (error) {
            console.error('Erro ao carregar preferências', error);
            console.log('Status:', error.response?.status);
            console.log('Resposta da API:', error.response?.data);
            console.log('Request:', error.config?.data);
        } finally {
            setLoading(false);
        }
    };

    const clearStates = () => {
        setItems([]);
        setUpdatedItems([]);
        setChecked(false);
    }

    const handleClose = (event, reason) => {
        if (reason === "clickaway") {
            return;
        }
        setOpen(false);
    };

    const handleChange = (event) => {
        const checkedValue = event.target.checked;

        setChecked(checkedValue);

        setUpdatedItems((prev) => {
            const exists = prev.some((item) => Number(item?.Type) === 1);
            if (exists) {
                return prev.map((item) =>
                    item?.Type === 1
                        ? {
                            ...item,
                            Value: checkedValue,
                        }
                        : item
                );
            }
            findNotReceivingOffersId()
            return [
                ...prev,
                {
                    Id: findNotReceivingOffersId(),
                    UserId: user.Id,
                    TennantId: null,
                    Description: null,
                    Type: 1,
                    Value: checkedValue,
                },
            ];
        });

        setIsEdit(true);
    };

    const findNotReceivingOffersId = () => {
        return items.find((item) => Number(item?.Type) === 1)?.Id ?? null;
    }

    const handleToggle = (index) => {
        setItems((prev) => {
            const newItems = prev.map((item, i) =>
                i === index
                    ? { ...item, Value: !item.Value }
                    : item
            );

            const changedItem = newItems[index];

            setUpdatedItems((upPrev) => {
                const exists = upPrev.some((x) => x.index === index);

                if (exists) {
                    return upPrev.map((x) =>
                        x.index === index
                            ? {
                                ...changedItem,
                                Type: 2,
                                index,
                            }
                            : {
                                ...x,
                                Type: 2,
                            }
                    );
                }

                return [
                    ...upPrev,
                    {
                        ...changedItem,
                        Type: 2,
                        index,
                    },
                ];
            });

            return newItems;
        });

        setIsEdit(true);
    };

    const handleSave = async () => {
        try {
            setIsEdit(false);
            const apiObject = updatedItems?.
                filter((item) => !(item?.Type === 2 && item?.Id === null && item?.Value === false))?.
                map(({ index, ...item }) => item);

            await userPreferencesService.CreateUserPreferences(apiObject, token)
                .then(() => {
                    setAlert({ type: 'success', message: 'Preferências atualizadas!' });
                    setOpen(true);
                    setSaved(!isSaved);
                })
                .catch((error) => {
                    setIsEdit(true);
                    throw error;
                })
                .finally(() => {
                    clearStates();
                })
        }
        catch (error) {
            setAlert({ type: 'error', message: `Erro ao salvar preferências: ${error.response.data.error[0]}` });
            setOpen(true);
            console.error("Erro ao salvar preferências:", { ...error });
            console.log('Status:', error.response?.status);
            console.log('Resposta da API:', error.response?.data);
            console.log('Request:', error.config?.data);
        }
    }

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" p={2}>
                <CircularProgress />
            </Box>
        );
    }

    const snackBar = (
        <Snackbar open={open} autoHideDuration={3000} onClose={handleClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
            <MuiAlert onClose={handleClose} severity={alert.type} sx={{ width: "100%" }}>
                {alert.message}
            </MuiAlert>
        </Snackbar>
    )

    return (
        <MDBox mt={2}>
            <Paper
                elevation={3}
                sx={{
                    backgroundColor: '#fff',
                    padding: 2,
                    borderRadius: 3,
                    width: '100%',
                }}
            >
                <FormControlLabel control={<Checkbox checked={checked} onChange={handleChange} />} label="Não receber ofertas de compra" />
                <Box mt={2}>
                    <Typography
                        variant="h6"
                        sx={{
                            mb: 1,
                            fontWeight: 'bold',
                        }}
                    >
                        Marque os clientes que não deseja receber ofertas de compra
                    </Typography>
                    <List
                        sx={{
                            width: '100%',
                            bgcolor: 'background.paper',

                            maxHeight: 600,
                            overflowY: 'auto',

                            border: '1px solid #ddd',
                            borderRadius: 2,

                            p: 1,
                        }}
                    >
                        {items.map((item, index) =>
                            (item?.Type ?? 2) === 2 && (
                                <ListItem
                                    key={index}
                                    divider
                                    sx={{
                                        width: "96%",
                                        margin: "0 auto",
                                        borderRadius: 1,
                                    }}
                                    secondaryAction={
                                        <Checkbox
                                            edge="end"
                                            checked={item?.Value ?? false}
                                            onChange={() => handleToggle(index)}
                                        />
                                    }
                                >
                                    <ListItemText
                                        primaryTypographyProps={{
                                            fontSize: "0.95rem",
                                            fontWeight: 400,
                                            letterSpacing: "0.3px",
                                            fontFamily: '"Segoe UI", sans-serif',
                                            color: "#444",
                                        }}
                                        primary={`${item?.TennantId ?? ""} - ${item?.Description ?? ""}`}
                                    />
                                </ListItem>
                            )
                        )}
                    </List>
                </Box>
                <Button
                    sx={{ mt: 2 }}
                    variant="contained"
                    onClick={handleSave}
                    disabled={!isEdit}
                >
                    <MDBox color="white">Salvar</MDBox>
                </Button>
                {snackBar}
            </Paper>
        </MDBox>
    )
}